import { create } from 'zustand'
import { driveService } from '../services/googleDrive'
import { isAIConfigured } from './aiStore'
import {
  fetchWebsiteData,
  fetchCompaniesHouseData,
  generateResearchSummary,
  buildCompanyIntelligence,
  buildPartialIntelligence,
  ResearchError,
} from '../services/research'
import type {
  CompanyIntelligence,
  ResearchState,
  ResearchSettings,
} from '../types/research'
import { DEFAULT_RESEARCH_SETTINGS } from '../types/research'

const RESEARCH_SETTINGS_FILE = 'research-settings.json'
const RESEARCH_FILE = 'research.json'

function extractDomain(input: string | undefined): string {
  if (!input) return ''
  return input
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/.*$/, '')
    .trim()
}

function getCompanyDomain(company: { domain?: string; website?: string }): string {
  return company.domain || extractDomain(company.website)
}

interface ResearchData {
  [companyId: string]: CompanyIntelligence
}

interface ResearchStoreState {
  // Per-company research state
  researchStates: Map<string, ResearchState>
  
  // All intelligence data
  intelligence: ResearchData
  isHydrated: boolean
  
  // Settings
  settings: ResearchSettings
  settingsLoaded: boolean
  
  // Actions
  hydrate: () => Promise<void>
  loadSettings: () => Promise<void>
  saveSettings: (settings: ResearchSettings) => Promise<void>
  
  runResearch: (
    company: { id: string; name: string; domain?: string },
    options?: { includeCompaniesHouse?: boolean }
  ) => Promise<CompanyIntelligence | null>
  
  getResearchState: (companyId: string) => ResearchState
  getIntelligence: (companyId: string) => CompanyIntelligence | null
  clearIntelligence: (companyId: string) => Promise<void>
}

const DEFAULT_RESEARCH_STATE: ResearchState = {
  status: 'idle',
  progress: {
    website: 'pending',
    companiesHouse: 'pending',
    ai: 'pending',
  },
}

export const useResearchStore = create<ResearchStoreState>((set, get) => ({
  researchStates: new Map(),
  intelligence: {},
  isHydrated: false,
  settings: DEFAULT_RESEARCH_SETTINGS,
  settingsLoaded: false,

  hydrate: async () => {
    try {
      const data = await driveService.readEntity<ResearchData>(RESEARCH_FILE)
      if (data) {
        set({ intelligence: data, isHydrated: true })
      } else {
        set({ isHydrated: true })
      }
    } catch (error) {
      console.error('Failed to load research data:', error)
      set({ isHydrated: true })
    }
  },

  loadSettings: async () => {
    try {
      const settings = await driveService.readSettings<ResearchSettings>(RESEARCH_SETTINGS_FILE)
      if (settings) {
        set({ settings, settingsLoaded: true })
      } else {
        set({ settingsLoaded: true })
      }
    } catch (error) {
      console.error('Failed to load research settings:', error)
      set({ settingsLoaded: true })
    }
  },

  saveSettings: async (settings: ResearchSettings) => {
    try {
      await driveService.writeSettings(RESEARCH_SETTINGS_FILE, settings)
      set({ settings })
    } catch (error) {
      console.error('Failed to save research settings:', error)
      throw error
    }
  },

  runResearch: async (company, options = {}) => {
    const { settings, researchStates, intelligence } = get()
    const includeCompaniesHouse = options.includeCompaniesHouse ?? settings.includeCompaniesHouse

    const domain = getCompanyDomain(company)

    // Initialise state
    const state: ResearchState = {
      status: 'fetching-website',
      progress: {
        website: domain ? 'pending' : 'skipped',
        companiesHouse: includeCompaniesHouse ? 'pending' : 'skipped',
        ai: 'pending',
      },
    }

    const updateState = (updates: Partial<ResearchState>) => {
      const newState = { ...state, ...updates }
      Object.assign(state, newState)
      const newStates = new Map(researchStates)
      newStates.set(company.id, newState)
      set({ researchStates: newStates })
    }

    updateState(state)

    const rawData = {
      website: null as Awaited<ReturnType<typeof fetchWebsiteData>>,
      companiesHouse: null as Awaited<ReturnType<typeof fetchCompaniesHouseData>>,
    }

    // 1. Fetch website data
    if (domain) {
      updateState({ status: 'fetching-website' })

      try {
        rawData.website = await fetchWebsiteData(domain)
        updateState({
          progress: { ...state.progress, website: rawData.website ? 'success' : 'failed' },
        })
      } catch (error) {
        console.error('Website fetch error:', error)
        updateState({
          progress: { ...state.progress, website: 'failed' },
          error: error instanceof ResearchError ? error.message : 'Website fetch failed',
        })
      }
    }

    // 2. Fetch Companies House data
    if (includeCompaniesHouse) {
      updateState({ status: 'fetching-companies-house' })

      try {
        rawData.companiesHouse = await fetchCompaniesHouseData(company.name)

        // API auto-selects high-confidence matches (>= 90% similarity)
        // If we got a company back, we're good
        // If we only got matches, the confidence was too low - skip for now
        const hasData = rawData.companiesHouse?.company
        const wasAutoSelected = rawData.companiesHouse?.autoSelected
        const notConfigured = rawData.companiesHouse?.notConfigured
        
        if (hasData) {
          if (wasAutoSelected) {
            console.log('[ COMPANIES HOUSE: Auto-selected high-confidence match ]')
          }
          updateState({
            progress: { ...state.progress, companiesHouse: 'success' },
          })
        } else if (notConfigured) {
          updateState({
            progress: { ...state.progress, companiesHouse: 'skipped' },
          })
        } else if (rawData.companiesHouse?.matches?.length) {
          // Matches found but none high-confidence enough for auto-selection
          console.log(`[ COMPANIES HOUSE: ${rawData.companiesHouse.matches.length} potential matches, none high-confidence ]`)
          updateState({
            progress: { ...state.progress, companiesHouse: 'skipped' },
          })
        } else {
          // No matches at all
          updateState({
            progress: { ...state.progress, companiesHouse: 'skipped' },
          })
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Companies House lookup failed'
        console.warn('Companies House lookup failed:', errorMsg)
        updateState({
          progress: { ...state.progress, companiesHouse: 'failed' },
          error: errorMsg,
        })
      }
    }

    // 3. Generate AI summary
    let companyIntelligence: CompanyIntelligence

    if (!isAIConfigured()) {
      updateState({
        progress: { ...state.progress, ai: 'skipped' },
      })
      companyIntelligence = buildPartialIntelligence(company, rawData)
    } else {
      updateState({ status: 'generating-summary' })

      try {
        const aiOutput = await generateResearchSummary(company, rawData, settings.businessContext)
        updateState({
          progress: { ...state.progress, ai: 'success' },
        })
        companyIntelligence = buildCompanyIntelligence(company, rawData, aiOutput)
      } catch (error) {
        console.error('AI summary error:', error)
        updateState({
          progress: { ...state.progress, ai: 'failed' },
          error: error instanceof ResearchError ? error.message : 'AI summary failed',
        })
        companyIntelligence = buildPartialIntelligence(company, rawData)
      }
    }

    // 4. Update state and save to Drive
    const newIntelligence = { ...intelligence, [company.id]: companyIntelligence }
    set({ intelligence: newIntelligence })
    updateState({ status: 'complete' })

    try {
      await driveService.writeEntity(RESEARCH_FILE, newIntelligence)
    } catch (error) {
      console.error('Failed to save research data:', error)
    }

    return companyIntelligence
  },

  getResearchState: (companyId: string) => {
    return get().researchStates.get(companyId) || DEFAULT_RESEARCH_STATE
  },

  getIntelligence: (companyId: string) => {
    return get().intelligence[companyId] || null
  },

  clearIntelligence: async (companyId: string) => {
    const { intelligence, researchStates } = get()

    // Remove from state
    const { [companyId]: _, ...newIntelligence } = intelligence

    // Reset research state
    const newStates = new Map(researchStates)
    newStates.delete(companyId)

    set({ intelligence: newIntelligence, researchStates: newStates })

    // Save to Drive
    try {
      await driveService.writeEntity(RESEARCH_FILE, newIntelligence)
    } catch (error) {
      console.error('Failed to save research data:', error)
    }
  },
}))

// Convenience functions
export function isResearching(companyId: string): boolean {
  const state = useResearchStore.getState().getResearchState(companyId)
  return (
    state.status !== 'idle' &&
    state.status !== 'complete' &&
    state.status !== 'error' &&
    state.status !== 'awaiting-company-selection'
  )
}
